import React, { useRef } from 'react';
import toastMessage from '../../../utils/toastMessage';
import {
  Content,
  Header,
} from '../EntityPermissionsWidgetPopover/EntityPermissionsWidgetPopoverUI';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { PrimaryButton } from '../../Button';
import { SvgIcon } from '../../Icon';
import { getScrollBarStyles } from '../../../config/styles';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { buttonClicked } from '../../../utils/analytics/events/button/events';
import copyTextToClipboard from '../../../utils/copyTextToClipboard';

const Container = styled.div`
  ${floatingComponentStyles};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  padding-right: 12px;
  padding-bottom: 12px;
  padding-left: 12px;
  min-height: 300px;
`;

const ContentHeader = styled(Header)`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 0;
`;

const ContentBody = styled(Content)`
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const StyledEmbedCodeInput = styled.textarea`
  width: 100%;
  padding: 16px;
  border: 1px solid ${theme.colors.secondary300};
  border-radius: 4px;
  font-size: 14px;
  flex: 1;
  background: ${theme.colors.level1};
  color: ${theme.colors.secondary800};
  ${getScrollBarStyles(2)};
  cursor: pointer;

  &:hover {
    border-color: ${theme.colors.mainBlue};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.mainBlue};
  }
`;

const Footer = styled.div`
  flex: 0 0 auto;
  text-align: right;
`;

interface IProps {
  embedCode: string;
  trackContext: 'dashboards' | 'reports';
  onClose: () => void;
}

const EmbedCodeContent = ({ embedCode, trackContext, onClose }: IProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { track } = useAnalytics();

  const onSelectEmbedCode = async () => {
    await copyTextToClipboard(embedCode);

    if (textareaRef.current) {
      textareaRef.current.select();
    }
  };

  const onCopyEmbedCode = async () => {
    await copyTextToClipboard(embedCode);

    toastMessage({
      message: 'Embed code copied!',
      type: 'info',
    });

    track(buttonClicked({ context: trackContext, action: 'copy_embed_code' }));

    // Blur the textarea after copying
    if (textareaRef.current) {
      textareaRef.current.blur();
    }
  };

  return (
    <Container>
      <ContentHeader>
        <SvgIcon
          name="caret-left"
          width={16}
          height={16}
          onClick={onClose}
          isHoverable={true}
        />
        Embed code
      </ContentHeader>
      <ContentBody>
        <StyledEmbedCodeInput
          ref={textareaRef}
          value={embedCode}
          readOnly
          onClick={onSelectEmbedCode}
        />
        <Footer>
          <PrimaryButton onClick={onCopyEmbedCode} type="button">
            Copy
          </PrimaryButton>
        </Footer>
      </ContentBody>
    </Container>
  );
};

export default EmbedCodeContent;
